/* SalesInbox — unified 5-stage inbox for Leads & Sales (2026-10-08).
 *
 * Why: the user asked for a tap-and-forget pipeline that works without CRM
 * jargon. This view groups every lead (regardless of legacy stage) into one
 * of 5 simplified buckets served by `GET /api/crm/inbox`:
 *   New → Contacted → Proposal → Won → Lost
 * Legacy stages (qualified / replied / discovery / proposal_sent /
 * negotiation / nurture / spam) are mapped automatically on the backend.
 *
 * Each card exposes one-click quick actions so Kaily can advance a lead
 * without opening the modal: WhatsApp · Contacted · Proposal · Won · Lost
 * · Snooze 3d. Clicking the body opens the (newly slimmed) Lead modal.
 *
 * Website inquiries flow in here because every `/api/inquiries` POST now
 * auto-creates a lead via `create_lead_from_inquiry` (lead_source =
 * "website_inquiry") — so the "New" column IS the primary website-inquiry
 * view. The Archive page remains as a read-only history.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Instagram, Mail, Globe, Sparkles, Clock } from "lucide-react";
import LeadDetail from "@/pages/studio/crm/LeadDetail";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLS = [
    { key: "new", label: "New", hint: "Website · WhatsApp · IG · ChatGPT · Manual" },
    { key: "contacted", label: "Contacted", hint: "Replied, discovery done, in conversation" },
    { key: "proposal", label: "Proposal", hint: "Quote sent, waiting for decision" },
    { key: "won", label: "Won", hint: "Convert to Client Project when ready" },
    { key: "lost", label: "Lost", hint: "Closed · searchable history" },
];

const SOURCE_LABEL = {
    website_inquiry: "Website",
    whatsapp: "WhatsApp",
    instagram: "Instagram",
    email: "Email",
    chatgpt_discovery: "ChatGPT",
    google_maps: "Maps",
    referral: "Referral",
    walk_in: "Walk-in",
    existing_client: "Existing",
    manual: "Manual",
    other: "Other",
};

const waLink = (phone, brand) => {
    const digits = (phone || "").replace(/[^+\d]/g, "");
    const text = encodeURIComponent(`Hi ${brand ? brand + " team" : "there"}, thanks for reaching out — this is Kaily from slowcafe.my`);
    return `https://wa.me/${digits.replace(/^\+/, "")}?text=${text}`;
};

export default function SalesInbox() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [cls] = useState("live");
    const [filter, setFilter] = useState("all"); // all / website / whatsapp / instagram / nurture / spam
    const [openId, setOpenId] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/crm/inbox`, { params: { cls } });
            setData(r.data);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Failed to load inbox");
        }
    }, [cls]);

    useEffect(() => { load(); }, [load]);

    const quick = async (lead, path, body = {}, successMsg = "") => {
        setBusyId(lead.id);
        try {
            await axios.post(`${API}/crm/leads/${lead.id}${path}`, body);
            if (successMsg) toast.success(successMsg);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action failed");
        } finally {
            setBusyId(null);
        }
    };

    const filtered = (bucket) => {
        if (filter === "all") return bucket;
        if (filter === "website") return bucket.filter((c) => (c.lead_source || "") === "website_inquiry");
        if (filter === "whatsapp") return bucket.filter((c) => (c.lead_source || "") === "whatsapp");
        if (filter === "instagram") return bucket.filter((c) => (c.lead_source || "") === "instagram");
        return bucket;
    };

    if (err) return <div className="text-sm text-red-600" data-testid="inbox-error">{err}</div>;
    if (!data) return <div className="text-sm text-cafe-mocha" data-testid="inbox-loading">Loading inbox…</div>;

    const { buckets, counts } = data;

    return (
        <div data-testid="sales-inbox">
            {/* Header */}
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div>
                    <h2 className="font-serif text-2xl md:text-3xl text-cafe-espresso leading-tight">Inbox</h2>
                    <p className="text-sm text-cafe-mocha mt-1">
                        Every incoming enquiry lives here — one lead, one card, one tap to move it forward.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {[
                        ["all", "All sources"],
                        ["website", "Website"],
                        ["whatsapp", "WhatsApp"],
                        ["instagram", "Instagram"],
                    ].map(([k, l]) => (
                        <button
                            key={k}
                            data-testid={`inbox-filter-${k}`}
                            onClick={() => setFilter(k)}
                            className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${filter === k ? "bg-cafe-espresso text-white border-cafe-espresso" : "bg-white text-cafe-mocha border-border hover:border-cafe-olive"}`}>
                            {l}
                        </button>
                    ))}
                    <Link
                        to="/studio?tab=settings&sub=archive"
                        data-testid="inbox-archive-link"
                        className="h-8 px-3 rounded-full text-xs font-semibold border border-border text-cafe-mocha bg-white hover:border-cafe-olive inline-flex items-center">
                        Archive →
                    </Link>
                </div>
            </div>

            {/* 5-stage columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="inbox-columns">
                {COLS.map((col) => {
                    const list = filtered(buckets[col.key] || []);
                    const total = (counts || {})[col.key] || 0;
                    const shown = list.length;
                    return (
                        <section key={col.key} className="bg-white border border-border rounded-2xl p-3 min-h-[260px]" data-testid={`col-${col.key}`}>
                            <header className="flex items-baseline justify-between mb-2">
                                <h3 className="text-[11px] uppercase tracking-[0.18em] font-bold text-cafe-espresso">
                                    {col.label}
                                </h3>
                                <span className="text-xs text-cafe-mocha" data-testid={`col-${col.key}-count`}>
                                    {shown === total ? total : `${shown}/${total}`}
                                </span>
                            </header>
                            <p className="text-[10px] text-cafe-mocha/80 mb-3">{col.hint}</p>
                            {list.length === 0 ? (
                                <div className="text-[11px] text-cafe-mocha/60 italic py-6 text-center">Empty</div>
                            ) : (
                                <ul className="space-y-2">
                                    {list.map((c) => (
                                        <LeadCard
                                            key={c.id}
                                            card={c}
                                            colKey={col.key}
                                            busy={busyId === c.id}
                                            onOpen={() => setOpenId(c.id)}
                                            onQuick={quick}
                                        />
                                    ))}
                                </ul>
                            )}
                        </section>
                    );
                })}
            </div>

            {/* Secondary buckets */}
            {(buckets.nurture?.length > 0 || buckets.spam?.length > 0) && (
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {["nurture", "spam"].map((k) => {
                        const list = buckets[k] || [];
                        if (list.length === 0) return null;
                        return (
                            <section key={k} className="bg-cafe-latte/40 border border-border rounded-2xl p-3" data-testid={`col-${k}`}>
                                <h3 className="text-[11px] uppercase tracking-[0.18em] font-bold text-cafe-espresso mb-3">{k === "nurture" ? "Nurture" : "Spam"} · {list.length}</h3>
                                <ul className="space-y-2">
                                    {list.map((c) => (
                                        <LeadCard key={c.id} card={c} colKey={k} busy={busyId === c.id} onOpen={() => setOpenId(c.id)} onQuick={quick} />
                                    ))}
                                </ul>
                            </section>
                        );
                    })}
                </div>
            )}

            {openId && (
                <LeadDetail leadId={openId} onClose={() => { setOpenId(null); load(); }} />
            )}
        </div>
    );
}

function LeadCard({ card: c, colKey, busy, onOpen, onQuick }) {
    const ct = c.contact || {};
    const src = SOURCE_LABEL[c.lead_source] || "Lead";
    const whenRel = relTime(c.last_contact || c.created_at);
    const canClose = colKey !== "lost" && colKey !== "won" && colKey !== "spam";
    return (
        <li className="bg-cafe-cream border border-border rounded-xl p-2.5 hover:border-cafe-olive transition-colors group" data-testid={`inbox-card-${c.id}`}>
            <button onClick={onOpen} className="w-full text-left">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <div className="font-semibold text-[13px] text-cafe-espresso truncate" title={c.brand}>{c.brand}</div>
                        <div className="text-[10.5px] text-cafe-mocha truncate mt-0.5">
                            <span className="font-mono">{src}</span>
                            {c.business_type && <> · {c.business_type}</>}
                        </div>
                    </div>
                    {c.value_rm > 0 && <span className="text-[10.5px] font-bold text-cafe-olive shrink-0">RM {c.value}</span>}
                </div>
                {c.followup_date && colKey !== "lost" && (
                    <div className={`mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold ${c.followup_status === "overdue" ? "text-red-700" : "text-cafe-mocha"}`}>
                        <Clock size={9} /> {c.followup_date}
                    </div>
                )}
            </button>
            {/* One-click toolbar */}
            <div className="flex items-center gap-1 mt-2 flex-wrap">
                {ct.phone && (
                    <a
                        href={waLink(ct.phone, c.brand)}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`card-wa-${c.id}`}
                        className="h-6 w-6 rounded-full border border-border bg-white inline-flex items-center justify-center hover:border-cafe-olive"
                        title="WhatsApp">
                        <MessageCircle size={11} />
                    </a>
                )}
                {ct.instagram && (
                    <a
                        href={ct.instagram.startsWith("http") ? ct.instagram : `https://instagram.com/${ct.instagram}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="h-6 w-6 rounded-full border border-border bg-white inline-flex items-center justify-center hover:border-cafe-olive"
                        title="Instagram">
                        <Instagram size={11} />
                    </a>
                )}
                {ct.email && (
                    <a
                        href={`mailto:${ct.email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="h-6 w-6 rounded-full border border-border bg-white inline-flex items-center justify-center hover:border-cafe-olive"
                        title="Email">
                        <Mail size={11} />
                    </a>
                )}
                <span className="flex-1" />
                {colKey === "new" && (
                    <button
                        disabled={busy}
                        data-testid={`card-contacted-${c.id}`}
                        onClick={() => onQuick(c, "/stage", { stage: "contacted" }, "Marked Contacted")}
                        className="h-6 px-2 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-[10px] font-semibold hover:bg-blue-100 disabled:opacity-50">
                        Contacted
                    </button>
                )}
                {(colKey === "new" || colKey === "contacted") && (
                    <button
                        disabled={busy}
                        data-testid={`card-proposal-${c.id}`}
                        onClick={() => onQuick(c, "/stage", { stage: "proposal_sent" }, "Moved to Proposal")}
                        className="h-6 px-2 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-semibold hover:bg-amber-100 disabled:opacity-50">
                        Proposal
                    </button>
                )}
                {canClose && (
                    <>
                        <button
                            disabled={busy}
                            data-testid={`card-won-${c.id}`}
                            onClick={() => onQuick(c, "/won", {}, "Marked Won — convert to Client Project in modal")}
                            className="h-6 px-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-semibold hover:bg-emerald-100 disabled:opacity-50">
                            Won
                        </button>
                        <button
                            disabled={busy}
                            data-testid={`card-lost-${c.id}`}
                            onClick={() => onQuick(c, "/lost", {}, "Closed — removed from Overview")}
                            className="h-6 px-2 rounded-full bg-red-50 border border-red-200 text-red-700 text-[10px] font-semibold hover:bg-red-100 disabled:opacity-50">
                            Lost
                        </button>
                    </>
                )}
                {colKey === "won" && !c.converted_project_id && (
                    <span className="text-[10px] italic text-cafe-olive inline-flex items-center gap-1">
                        <Sparkles size={9} /> Convert in modal
                    </span>
                )}
            </div>
        </li>
    );
}

function relTime(iso) {
    if (!iso) return "";
    try {
        const d = typeof iso === "string" ? new Date(iso) : iso;
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return "just now";
        if (diff < 3600) return `${Math.floor(diff / 60)}m`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
        return `${Math.floor(diff / 86400)}d`;
    } catch { return ""; }
}
